import { defineStore } from "pinia"
import { computed, ref } from "vue"
import * as authApi from "@/api/admin/auth"
import type { AuthResponse, User } from "@/api/shared/types"

export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem("token"))
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => {
    const r = (user.value?.role ?? "").toLowerCase()
    return r === "superadmin" || r === "admin"
  })
  const userName = computed(() => user.value?.username ?? "User")
  const userEmail = computed(() => user.value?.email ?? "")

  async function login(email: string, password: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response: AuthResponse = await authApi.login({ email, password })

      token.value = response.token
      user.value = response.user

      localStorage.setItem("token", response.token)

      return true
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      error.value = err.response?.data?.message || "Login failed. Please check your credentials."
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function register(name: string, email: string, password: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      await authApi.register({ name, email, password })
      return true
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      error.value = err.response?.data?.message || "Registration failed. Please try again."
      return false
    } finally {
      isLoading.value = false
    }
  }

  /** Hindari beberapa getCurrentUser paralel (router + layout) saat refresh */
  let checkAuthPromise: Promise<boolean> | null = null

  async function checkAuth(): Promise<boolean> {
    if (!token.value) {
      return false
    }
    if (user.value) {
      return true
    }
    if (checkAuthPromise) {
      return checkAuthPromise
    }

    isLoading.value = true
    checkAuthPromise = (async () => {
      try {
        const currentUser = await authApi.getCurrentUser()
        user.value = currentUser
        return true
      } catch {
        await logout()
        return false
      } finally {
        isLoading.value = false
        checkAuthPromise = null
      }
    })()

    return checkAuthPromise
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } catch {
    } finally {
      user.value = null
      token.value = null
      localStorage.removeItem("token")
    }
  }

  async function updateProfile(data: Partial<User>): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const updatedUser = await authApi.updateProfile(data)
      user.value = updatedUser
      return true
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      error.value = err.response?.data?.message || "Failed to update profile"
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function changePassword(currentPassword: string, newPassword: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      })
      return true
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      error.value = err.response?.data?.message || "Failed to change password"
      return false
    } finally {
      isLoading.value = false
    }
  }

  function clearError(): void {
    error.value = null
  }

  return {
    user,
    token,
    isLoading,
    error,
    isAuthenticated,
    isAdmin,
    userName,
    userEmail,
    login,
    register,
    checkAuth,
    logout,
    updateProfile,
    changePassword,
    clearError,
  }
})
